import {
	BlinkSpeed,
	BlinkTime,
	LedColor,
	Market,
	Root,
	useMeQuery,
} from '@app/lib/graphql'
import { createContext, PropsWithChildren, useMemo, useState } from 'react'
import { useStickyState } from '@app/lib/packages/stickyState'
import {
	CurrentUserContextState,
	CurrentUserStickyState,
	currentUserStickyStateKey,
} from './types'

const initialContextState: CurrentUserContextState = {
	permissions: [],
	setDefaultUserSettings: () => {},
	updateUserSettings: () => {},
	deleteDefaultUserSettings: () => {},
}

export const CurrentUserContext = createContext(initialContextState)

export const CurrentUserProvider = ({ children }: PropsWithChildren<any>) => {
	const { data, loading } = useMeQuery()
	const [currentUserStickyState, setCurrentUserStickyState] =
		useStickyState<CurrentUserStickyState>(currentUserStickyStateKey)
	const [currentUserSecureState, setCurrentUserSecureState] =
		useState<CurrentUserStickyState | null>(null)

	const setDefaultUserSettings = (
		newDefaultRoot?: Root | null,
		filters?: any,
		tableName?: string,
		newDefaultMarket?: Market | null
	) => {
		console.log('newDefaultMarket', newDefaultMarket)
		if (tableName && filters) {
			setCurrentUserStickyState({
				...(currentUserStickyState || {}),
				defaultRoot:
					newDefaultRoot || newDefaultRoot === null
						? newDefaultRoot
						: currentUserStickyState?.defaultRoot,
				filters: {
					...currentUserStickyState?.filters,
					[tableName]: filters,
				},
				defaultMarket:
					newDefaultMarket || newDefaultMarket === null
						? newDefaultMarket
						: currentUserStickyState?.defaultMarket,
			})
		} else {
			setCurrentUserStickyState({
				...(currentUserStickyState || {}),
				defaultRoot:
					newDefaultRoot || newDefaultRoot === null
						? newDefaultRoot
						: currentUserStickyState?.defaultRoot,
				defaultMarket:
					newDefaultMarket || newDefaultMarket === null
						? newDefaultMarket
						: currentUserStickyState?.defaultMarket,
			})
		}
	}

	const deleteDefaultUserSettings = () => {
		setCurrentUserStickyState({
			...(currentUserStickyState || {}),
			defaultRoot: undefined,
			filters: undefined,
			defaultMarket: undefined,
		})
	}

	const updateUserSettings = (userSettings: CurrentUserStickyState) => {
		setCurrentUserSecureState((prev) => {
			return {
				...prev,
				...userSettings,
			}
		})
	}

	const values = useMemo<CurrentUserContextState>(() => {
		if (data?.me) {
			const {
				permissions,
				allowedIPs,
				roles,
				rootIDs,
				ledConfig,
				microsoftAuth,
				googleAuth,
				...otherProps
			} = data.me

			return {
				currentUser: {
					...otherProps,
					allowedIPs: allowedIPs || [],
					defaultRoot:
						currentUserStickyState?.defaultRoot || undefined,
					defaultMarket:
						currentUserStickyState?.defaultMarket || undefined,
					filters: currentUserStickyState?.filters,
					roles: roles || [],
					rootIDs: rootIDs || [],
					ledConfig: ledConfig ?? {
						blinkSpeed: BlinkSpeed.Standard,
						blinkTime: BlinkTime.Standard,
						color: LedColor.Red,
					},
					microsoftAuth:
						currentUserSecureState?.microsoftAuth !== undefined
							? currentUserSecureState?.microsoftAuth
							: microsoftAuth ?? null,
					googleAuth:
						currentUserSecureState?.googleAuth !== undefined
							? currentUserSecureState?.googleAuth
							: googleAuth ?? null,
				},
				permissions: permissions || [],
				setDefaultUserSettings,
				updateUserSettings,
				deleteDefaultUserSettings,
			}
		}

		return initialContextState
	}, [data, currentUserSecureState, currentUserStickyState])

	return (
		<CurrentUserContext.Provider value={values}>
			{loading ? null : <>{children}</>}
		</CurrentUserContext.Provider>
	)
}
